import React, { useMemo, useState } from 'react';
import { SanityBlock } from '@dtp/membership-service-types/dist/sanity/sanity';
import { TextVariant } from '@naf/text';
import BlockContent from '../../../BlockContent';
import { BlockContentContext } from '../../../BlockContent.utils';
import { FormBlock } from '../../Form/FormBlock';
import { DynamicForm } from '../../Form/FormBlock.types';
import { ArticleBlockContentGridCol } from './ArticleBlockContentGridCol';
import * as S from './DynamicFormSelectorSerializer.styles';

interface DynamicFormSelectorItem {
  key: string;
  value?: string;
  textIfSelected?: SanityBlock[];
}

interface DynamicFormSelector {
  title: string;
  items: DynamicFormSelectorItem[];
}

type DynamicForms = Record<string, DynamicForm>;

interface DynamicFormSelectorData {
  dynamicFormSelector?: DynamicFormSelector;
  dynamicForms?: DynamicForms;
}

interface DynamicFormSelectorSerializerProps {
  value: {
    data?: DynamicFormSelectorData;
  };
}

export const DynamicFormSelectorBlock = ({ value }: DynamicFormSelectorSerializerProps) => {
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);

  const dynamicFormSelector = value?.data?.dynamicFormSelector;
  const dynamicForms = useMemo(() => value?.data?.dynamicForms ?? {}, [value?.data?.dynamicForms]);
  const items = useMemo(() => dynamicFormSelector?.items || [], [dynamicFormSelector?.items]);

  const selectedDynamicForm = useMemo(
    () => (selectedItemKey ? dynamicForms[selectedItemKey] : undefined),
    [dynamicForms, selectedItemKey],
  );

  if (!items.length) {
    return null;
  }

  return (
    <S.Wrapper>
      <S.Title variant={TextVariant.Header2} tag="h2">
        {dynamicFormSelector.title}
      </S.Title>
      <S.Options role="radiogroup" aria-label={dynamicFormSelector.title}>
        {items.map((item) => {
          const isSelected = selectedItemKey === item.key;

          return (
            <S.OptionWrapper key={item.key}>
              <S.StyledRadioButton
                $isSelected={isSelected}
                label={item.value}
                onChange={() => setSelectedItemKey(item.key)}
                checked={isSelected}
              />
              {(isSelected && item.textIfSelected && (
                <S.TextIfSelectedWrapper>
                  <BlockContent value={item.textIfSelected} />
                </S.TextIfSelectedWrapper>
              )) ||
                null}
            </S.OptionWrapper>
          );
        })}
      </S.Options>
      {selectedDynamicForm ? (
        <S.DynamicFormWrapper>
          <FormBlock value={{ dynamicForm: selectedDynamicForm }} />
        </S.DynamicFormWrapper>
      ) : null}
    </S.Wrapper>
  );
};

export const DynamicFormSelectorSerializer = (context: BlockContentContext = BlockContentContext.Stripped) => {
  if (context === BlockContentContext.Article) {
    return ({ value }: DynamicFormSelectorSerializerProps) => (
      <ArticleBlockContentGridCol>
        <DynamicFormSelectorBlock value={value} />
      </ArticleBlockContentGridCol>
    );
  }

  return ({ value }: DynamicFormSelectorSerializerProps) => <DynamicFormSelectorBlock value={value} />;
};
